import { APPROVAL_TRACKS, latestApprovalSlotsByKey } from './changeApprovalPlan';
import { labelChangeStatus, labelChangeStatusTransition } from './changeLabels';

export interface StatusWaitBlock {
  status: string;
  reason: string;
}

export interface StatusWaitMissingSource {
  status?: string | null;
  approvals?: Array<{
    status?: string | null;
    group_id?: number | null;
    group_name?: string | null;
    track?: string | null;
  }> | null;
  subtasks?: Array<{
    id?: number;
    title?: string | null;
    status?: string | null;
    origin?: string | null;
    gates_implementar?: boolean;
  }> | null;
}

const AUTORIZAR_TRACKS = new Set([
  'riscos_impactos',
  'camud_go_no_go',
  'gestao_mudancas',
  'emergencial',
  'criador_gestor',
]);

function labelPendingApproval(slot: {
  group_id?: number | null;
  group_name?: string | null;
  track?: string | null;
}): string {
  const track = String(slot.track || '');
  const trackLabel =
    APPROVAL_TRACKS.find((item) => item.track === track)?.label ||
    (track === 'transporte' ? 'Transporte' : track === 'emergencial' ? 'Emergencial' : track === 'criador_gestor' ? 'Criador / gestor' : '');
  const name =
    String(slot.group_name || '').trim() ||
    (track === 'gestao_mudancas' && !slot.group_id
      ? 'Gestor de mudanças'
      : slot.group_id
        ? `área #${slot.group_id}`
        : 'área');
  if (!trackLabel || trackLabel === name) return name;
  return `${name} (${trackLabel})`;
}

/** Itens concretos ainda pendentes — o que o usuário precisa ler no aviso. */
export function listStatusWaitMissing(change: StatusWaitMissingSource | null | undefined): string[] {
  if (!change) return [];
  const status = String(change.status || '');
  if (status === 'autorizar') {
    return (change.approvals || [])
      .filter((slot) => {
        if (String(slot.status || '') !== 'pendente') return false;
        const track = String(slot.track || '');
        if (track && !AUTORIZAR_TRACKS.has(track)) return false;
        return Boolean(slot.group_id) || track === 'gestao_mudancas' || track === 'criador_gestor';
      })
      .map(labelPendingApproval);
  }
  if (status === 'transporte') {
    const latest = latestApprovalSlotsByKey(change.approvals || [], { track: 'transporte' });
    return latest
      .filter((slot) => String(slot.status || '') !== 'aprovado')
      .map((slot) => labelPendingApproval(slot));
  }
  if (status === 'implementar') {
    const items: string[] = [];
    for (const subtask of change.subtasks || []) {
      if (String(subtask.status || '') === 'concluida') continue;
      const title = String(subtask.title || '').trim();
      const isExternal =
        String(subtask.origin || '') === 'incidente' || Boolean(subtask.gates_implementar);
      const kind = isExternal ? 'externa' : 'interna';
      items.push(title ? `${title} (${kind})` : kind);
    }
    return items;
  }
  return [];
}

/** Mesmo mapa do backend: o select não oferece esses alvos. */
export const AUTOMATIC_NEXT: Record<string, string> = {
  autorizar: 'transporte',
  transporte: 'implementar',
  implementar: 'revisar',
};

export function forwardBlocked(blocked: StatusWaitBlock[] = []) {
  return blocked.filter((item) => item.status && item.status !== 'cancelada');
}

export function describeStatusFlow(
  currentStatus: string,
  allowed: string[] = [],
  blocked: StatusWaitBlock[] = [],
) {
  const currentLabel = labelChangeStatus(currentStatus);
  const autoNext = AUTOMATIC_NEXT[currentStatus];
  if (autoNext) {
    const nextLabel = labelChangeStatus(autoNext);
    return {
      currentLabel,
      nextLabel,
      automatic: true,
      summary: `Atual: ${currentLabel} → próximo: ${nextLabel} (automático)`,
    };
  }
  const intendedAllowed = allowed.filter((status) => status !== 'cancelada');
  const intendedBlocked = forwardBlocked(blocked);
  if (intendedAllowed.length) {
    const nextStatuses = allowed.includes('cancelada')
      ? [...intendedAllowed, 'cancelada']
      : intendedAllowed;
    const nextLabel = nextStatuses.map((status) => labelChangeStatusTransition(status)).join(' ou ');
    return {
      currentLabel,
      nextLabel,
      automatic: false,
      summary: `Atual: ${currentLabel} → próximo: ${nextLabel}`,
    };
  }
  if (intendedBlocked.length) {
    const nextLabel = labelChangeStatus(intendedBlocked[0].status);
    return {
      currentLabel,
      nextLabel,
      automatic: false,
      summary: `Atual: ${currentLabel} → próximo: ${nextLabel} (ainda não liberado)`,
    };
  }
  if (allowed.length) {
    const nextLabel = allowed.map((status) => labelChangeStatusTransition(status)).join(' ou ');
    return {
      currentLabel,
      nextLabel,
      automatic: false,
      summary: `Atual: ${currentLabel} → próximo: ${nextLabel}`,
    };
  }
  return {
    currentLabel,
    nextLabel: null as string | null,
    automatic: false,
    summary: `Atual: ${currentLabel} → sem próximo status`,
  };
}

const MISSING_LIST_PREFIX = /^(.*?)((?:Ainda falta|Ainda em aberto):)\s*(.*)$/is;

export function splitWaitItems(raw: string): string[] {
  const trimmed = String(raw || '')
    .replace(/\.$/, '')
    .trim();
  if (!trimmed) return [];
  if (/\)\s*,\s*/.test(trimmed)) {
    return trimmed
      .split(/\)\s*,\s*/)
      .map((part, index, all) => {
        const needsParen = index < all.length - 1 && !part.endsWith(')');
        return (needsParen ? `${part})` : part).trim();
      })
      .filter(Boolean);
  }
  if (trimmed.includes(', ')) {
    return trimmed.split(', ').map((item) => item.trim()).filter(Boolean);
  }
  return [trimmed];
}

export function formatWaitReason(reason: string): { intro: string; items: string[] } {
  const text = String(reason || '').trim();
  const match = text.match(MISSING_LIST_PREFIX);
  if (!match) {
    return { intro: text, items: [] };
  }
  const before = match[1].replace(/\s+/g, ' ').trim();
  const label = match[2].replace(/\s+/g, ' ').trim();
  const items = splitWaitItems(match[3]);
  return {
    intro: [before, label].filter(Boolean).join(' '),
    items,
  };
}

function composeWaitMessage(summary: string, reason: string, fallbackItems: string[] = []): string {
  const { intro, items } = formatWaitReason(reason);
  const listed = items.length ? items : fallbackItems;
  const lines = [summary];
  if (intro) lines.push(intro);
  else if (listed.length) lines.push('Ainda falta:');
  if (listed.length) {
    lines.push(listed.map((item) => `• ${item}`).join('\n'));
  }
  return lines.filter(Boolean).join('\n\n');
}

function composeMissingOnlyMessage(summary: string, items: string[]): string {
  return [summary, 'Ainda falta:', items.map((item) => `• ${item}`).join('\n')].join('\n\n');
}

export function buildStatusWaitAlert(opts: {
  currentStatus: string;
  allowed: string[];
  blocked?: StatusWaitBlock[];
  /** Nomes concretos (áreas/gestor/subtarefas) — nunca substituir por frase vazia. */
  missingItems?: string[];
}): { title: string; message: string; details?: string } {
  const flow = describeStatusFlow(opts.currentStatus, opts.allowed, opts.blocked);
  const missingItems = (opts.missingItems || []).map((item) => String(item || '').trim()).filter(Boolean);
  const reasons = (opts.blocked || [])
    .map((item) => String(item.reason || '').trim())
    .filter(Boolean);

  if (reasons.length) {
    return {
      title: `Ainda não dá para sair de ${flow.currentLabel}`,
      message: composeWaitMessage(flow.summary, reasons[0], missingItems),
      details: reasons.length > 1 ? reasons.slice(1).join('\n') : undefined,
    };
  }

  if (missingItems.length) {
    return {
      title: `Ainda não dá para sair de ${flow.currentLabel}`,
      message: composeMissingOnlyMessage(flow.summary, missingItems),
    };
  }

  if (opts.currentStatus === 'encerrar' || opts.currentStatus === 'cancelada') {
    return {
      title: `Status: ${flow.currentLabel}`,
      message: `${flow.summary}. O registro fica no histórico.`,
    };
  }

  if ((opts.allowed || []).length) {
    return {
      title: `Você está em ${flow.currentLabel}`,
      message: `${flow.summary}. Selecione o próximo status no campo ao lado.`,
    };
  }

  // Status automático sem lista concreta: orientar onde olhar — nunca "quando o que falta for concluído".
  if (flow.automatic) {
    const whatToDo =
      opts.currentStatus === 'autorizar'
        ? 'Na aba Aprovações, veja quem ainda está como Pendente. Se todos já estiverem Aprovado, atualize a página para o sistema avançar sozinho.'
        : opts.currentStatus === 'transporte'
          ? 'Na aba Transporte, conclua as aprovações GTEC/GSERV-I. Subtarefa externa de Infra não precisa estar concluída para ir a Implementar — isso trava o avanço para Revisar. Se ainda não abriu o chamado, use Abrir chamado de Infra (gate de Implementar).'
          : opts.currentStatus === 'implementar'
            ? 'Na aba Subtarefas, conclua as internas e externas pendentes (incluindo o chamado de Infra). Sair de Transporte só exigiu as aprovações GTEC/GSERV-I.'
            : 'Veja a aba Aprovações ou Subtarefas para identificar o pendente.';
    return {
      title: `Ainda não dá para sair de ${flow.currentLabel}`,
      message: `${flow.summary}.\n\n${whatToDo}`,
    };
  }

  return {
    title: `Ainda não dá para sair de ${flow.currentLabel}`,
    message: `${flow.summary}. Não há transição liberada neste momento.`,
  };
}
